/**
 * Google Analytics 組合式函數
 * 提供事件追蹤和頁面瀏覽追蹤功能
 */
import { captureMarketingAttribution, GA_TRACKING_ID, getAnalyticsPageContext, loadAnalytics } from '@/utils/analytics'

interface GAEventParams {
  action: string
  category?: string
  label?: string
  value?: number
  custom_parameters?: Record<string, any>
}

interface GAPageViewParams {
  page_title?: string
  page_location?: string
  page_path?: string
}

let previousPageLocation = ''

export const useGoogleAnalytics = () => {
  /**
   * 檢查 gtag 是否可用
   */
  const isGtagAvailable = (): boolean => {
    return typeof window !== 'undefined' && typeof window.gtag === 'function'
  }

  /**
   * 追蹤自定義事件
   */
  const trackEvent = (eventName: string, parameters: GAEventParams) => {
    if (!GA_TRACKING_ID) return
    loadAnalytics()
    if (!isGtagAvailable()) {
      return
    }

    try {
      window.gtag?.('event', eventName, {
        event_category: parameters.category,
        event_label: parameters.label,
        value: parameters.value,
        ...parameters.custom_parameters,
        ...getAnalyticsPageContext(),
        send_to: GA_TRACKING_ID
      })

      console.log('GA Event tracked:', eventName)
    } catch (error) {
      console.error('Error tracking GA event:', error)
    }
  }

  /**
   * 追蹤頁面瀏覽
   */
  const trackPageView = (parameters: GAPageViewParams = {}) => {
    if (!GA_TRACKING_ID) return
    loadAnalytics()
    if (!isGtagAvailable()) {
      return
    }

    try {
      const pageContext = getAnalyticsPageContext(parameters.page_location, parameters.page_path)
      if (previousPageLocation) pageContext.page_referrer = previousPageLocation
      window.gtag?.('set', pageContext)
      window.gtag?.('event', 'page_view', {
        page_title: parameters.page_title || document.title,
        ...pageContext,
        send_to: GA_TRACKING_ID
      })
      previousPageLocation = pageContext.page_location

      console.log('GA Page view tracked:', pageContext.page_path)
    } catch (error) {
      console.error('Error tracking GA page view:', error)
    }
  }

  /**
   * 追蹤用戶註冊
   */
  const trackSignUp = (method: string = 'email') => {
    trackEvent('sign_up', {
      action: 'sign_up',
      category: 'engagement',
      custom_parameters: {
        method,
        ...captureMarketingAttribution()
      }
    })
  }

  /**
   * 追蹤用戶登入
   */
  const trackLogin = (method: string = 'email') => {
    trackEvent('login', {
      action: 'login',
      category: 'engagement',
      custom_parameters: {
        method
      }
    })
  }

  /**
   * 追蹤 VIP 訂閱
   */
  const trackPurchase = (transactionId: string, value: number, currency: string = 'USD') => {
    trackEvent('purchase', {
      action: 'purchase',
      category: 'ecommerce',
      value,
      custom_parameters: {
        transaction_id: transactionId,
        currency,
        items: [{
          item_id: 'vip_subscription',
          item_name: 'VIP Subscription',
          category: 'subscription',
          price: value,
          quantity: 1
        }]
      }
    })
  }

  /**
   * 追蹤搜索行為
   */
  const trackSearch = (searchTerm: string, results: number) => {
    trackEvent('search', {
      action: 'search',
      category: 'engagement',
      custom_parameters: {
        search_term: searchTerm,
        results_count: results
      }
    })
  }

  /**
   * 追蹤消息發送
   */
  const trackMessage = (messageType: 'first_message' | 'reply' | 'greeting') => {
    trackEvent('message_sent', {
      action: 'message_sent',
      category: 'engagement',
      custom_parameters: {
        message_type: messageType
      }
    })
  }

  /**
   * 追蹤檔案上傳
   */
  const trackUpload = (fileType: 'photo' | 'video', success: boolean) => {
    trackEvent('file_upload', {
      action: 'file_upload',
      category: 'engagement',
      custom_parameters: {
        file_type: fileType,
        success
      }
    })
  }

  return {
    trackEvent,
    trackPageView,
    trackSignUp,
    trackLogin,
    trackPurchase,
    trackSearch,
    trackMessage,
    trackUpload,
    isGtagAvailable
  }
}
